import React, { useEffect, useState } from 'react';
import { Button, FormInstance } from 'antd';
import {
  Drawer,
  TextArea,
  Icon,
  Gutter,
  Divider,
  Form,
} from '@common/components';
import { Colors } from '@common/tokens';
import { Spacing } from 'works/legacy/lib/style-helper';
import { createMessage } from 'works/legacy/lib/message-helper';
import { OfferDataTable } from 'works/legacy/components/offer-data-table';
import { useMutation } from '@common/hooks';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import {
  ChangeRequestDialogBox,
  OfferInfoSummary,
} from '@applicant-flow/components';
import {
  ExtensionOfferChangeResponseForm,
  ExtensionOfferChangeResponseFormValuesType,
} from './_extension-offer-change-response-form';
import { CreateResponseExtensionOfferAcceptance } from 'works/legacy/graphql/change-response';
import { datetime, isDatetime } from '@common/services/datetime';
import { showNotification } from '@common/services';
import {
  ExtensionOfferAssignmentType,
  ExtensionOfferType,
} from '@working-experience/graphql/queries/extension-offer-query';

export function ExtensionOfferChangeResponseDrawer({
  assignment,
  extensionOffer,
  offerChangeRequestId,
  visible,
  onDrawerClose = () => {},
  edit,
}: {
  assignment: ExtensionOfferAssignmentType;
  extensionOffer: ExtensionOfferType;
  offerChangeRequestId?: string;
  visible: boolean;
  onDrawerClose?: () => void;
  edit?: boolean;
}) {
  const CHANGE_RESPONSE_TEXT_MAX_LENGTH = 160;

  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);

  const application = assignment.staffingRequestApplication;

  const [submitExtensionOfferChangeResponse, { loading }] = useMutation(
    CreateResponseExtensionOfferAcceptance,
    {
      onCompleted: (response) => {
        if (response.createResponseExtensionOfferAcceptance.errors) {
          addErrorsToForm(
            form,
            response.createResponseExtensionOfferAcceptance.errors
          );
        } else {
          createMessage('success', 'Change response has been submitted.');
          onClose();
        }
      },
      onError: () => {
        createMessage('error', 'Failed to perform action. Please try again.');
      },
      refetchQueries: ['CandidateAssignmentQuery'],
    }
  );

  const initialValues = {
    endDate: datetime(extensionOffer.endDate),
    timeoffDescription: extensionOffer.timeoffDescription,
    shiftType: extensionOffer.shiftType,
    shiftsPerWeek: extensionOffer.shiftsPerWeek,
    hoursPerShift: extensionOffer.hoursPerShift,
  };

  function hasFormAnyError(
    form: FormInstance<ExtensionOfferChangeResponseFormValuesType>
  ) {
    const formValues = form.getFieldsValue();
    const errors = Object.values(formValues).filter((v) => !v);

    if (!edit) {
      return errors.length > 0;
    }

    const editedFields = Object.entries(initialValues).filter(
      ([key, value]) => {
        if (key === 'endDate' && isDatetime(value)) {
          return !value.isSame(formValues[key]);
        }

        return (
          value !==
          formValues[key as keyof ExtensionOfferChangeResponseFormValuesType]
        );
      }
    );

    return errors.length > 0 || editedFields.length === 0;
  }

  function onChange() {
    setTimeout(() => {
      const hasError = hasFormAnyError(form);
      setIsFormValid(!hasError);
    });
  }

  function onClose() {
    form.resetFields();
    setIsFormValid(false);
    onDrawerClose();
  }

  function saveOfferChangeResponse(
    valuesFromForm: ExtensionOfferChangeResponseFormValuesType
  ) {
    const changeRequests = [...extensionOffer.offerChangeRequests].sort(
      (e1, e2) =>
        new Date(e1.createdAt).getTime() - new Date(e2.createdAt).getTime()
    );
    const lastChangeRequest = changeRequests[changeRequests.length - 1];

    const input = edit
      ? {
          shiftType: valuesFromForm.shiftType,
          hoursPerShift: valuesFromForm.hoursPerShift,
          shiftsPerWeek: valuesFromForm.shiftsPerWeek,
          timeoffDescription: valuesFromForm.timeoffDescription,
          note: valuesFromForm.offerChangeResponseMessage,
          endDate: valuesFromForm.endDate,
          billingRegularRate: extensionOffer.billingRegularRate,
        }
      : {
          shiftType: extensionOffer.shiftType,
          hoursPerShift: extensionOffer.hoursPerShift,
          shiftsPerWeek: extensionOffer.shiftsPerWeek,
          timeoffDescription: extensionOffer.timeoffDescription,
          note: valuesFromForm.offerChangeResponseMessage,
          endDate: extensionOffer.endDate,
          billingRegularRate: extensionOffer.billingRegularRate,
        };

    submitExtensionOfferChangeResponse({
      variables: {
        offerChangeRequestId: lastChangeRequest.id,
        input,
      },
    });
  }

  const changeRequests = extensionOffer.offerChangeRequests;
  const lastChangeRequest = changeRequests[changeRequests.length - 1];

  const selectedChangeRequest = extensionOffer.offerChangeRequests.find(
    (cr: { id: string }) => cr.id === offerChangeRequestId
  );
  useEffect(() => {
    if (offerChangeRequestId && !selectedChangeRequest) {
      showNotification({
        message:
          'The supplier retracted the change request. No changes are needed.',

        icon: <Icon kind="light" icon="circleInfo" color="elm" />,
        duration: 5,
      });
    }
  }, [selectedChangeRequest, offerChangeRequestId]);

  return (
    <>
      <Drawer
        className="extension-offer-change-response-drawer"
        open={visible}
        title={edit ? 'Update Extension Offer' : 'Reject Changes'}
        onClose={onClose}
        footer={
          <div className="extension-offer-change-response-drawer-footer">
            <Button
              key="cancelChangeResponseButton"
              size="large"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              key="submitChangeResponseButton"
              size="large"
              loading={loading}
              disabled={!isFormValid}
              onClick={form.submit}
            >
              {edit ? 'Send Updated Extension Offer' : 'Reply'}
            </Button>
          </div>
        }
      >
        {lastChangeRequest && (
          <Gutter direction="bottom" size="md">
            <ChangeRequestDialogBox changeRequest={lastChangeRequest} />
          </Gutter>
        )}
        <Form
          layout="vertical"
          form={form}
          onFinish={saveOfferChangeResponse}
          onChange={onChange}
          initialValues={initialValues}
        >
          <Form.Item
            label="Reply"
            name="offerChangeResponseMessage"
            rules={[
              {
                required: true,
                message: 'Please enter a reply',
              },
              {
                type: 'string',
                min: 1,
                max: CHANGE_RESPONSE_TEXT_MAX_LENGTH,
                message: `Reply must be fewer than ${CHANGE_RESPONSE_TEXT_MAX_LENGTH} characters`,
              },
            ]}
          >
            <TextArea
              name="offerChangeResponseMessage"
              maxLength={CHANGE_RESPONSE_TEXT_MAX_LENGTH}
            />
          </Form.Item>
        </Form>
        <Divider gutter={16} />
        {edit ? (
          <>
            <OfferInfoSummary
              application={application}
              rate={extensionOffer.billingRegularRate.toFixed(2)}
            />
            <ExtensionOfferChangeResponseForm
              form={form}
              onFinish={saveOfferChangeResponse}
              onChange={onChange}
              assignment={assignment}
              application={application}
              extensionOffer={extensionOffer}
            />
          </>
        ) : (
          <OfferDataTable application={application} offer={extensionOffer} />
        )}
      </Drawer>
      <style jsx>{`
        :global(.extension-offer-change-response-drawer-footer .ant-btn) {
          margin-right: ${Spacing.baseSpacing / 2}px;
        }

        :global(.OfferChangeResponseDrawer hr) {
          border: 0;
          border-top: 1px solid ${Colors.steel};
          margin-bottom: 15px;
        }
      `}</style>
    </>
  );
}
